import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError, AxiosResponse } from 'axios';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import { toast } from 'react-toastify';
import { RootThunkAPI } from 'app/store';
import {
  IAddParticipantsToCompetitions,
  IChangeCompetition,
  ICreateNewCompetition,
  MChangeCompetitionStatus,
} from './types';
import { mainServiceWithCredentials } from '../../shared/services/http';
import { competitionsB2BApi } from './api';

type ResponseType = IAddParticipantsToCompetitions.Response;

type RootThunkApiWithReject = RootThunkAPI & {
  rejectWithValue: (value: unknown) => unknown;
};

const handleErrorThunk = (e: any, thunkAPI: RootThunkApiWithReject, isCloseDrawer = false) => {
  thunkAPI.rejectWithValue(e);
  const { data, status } = e.response;
  const { detail } = data;

  if (status === 500) {
    toast.error('Возникла непредвиденная ошибка. Пожалуйста, попробуйте еще раз.');
  }

  if (isCloseDrawer) {
    thunkAPI.dispatch(setDrawerOpen(false));
  }

  return { detail, status };
};

const createNewCompetitionThunk = createAsyncThunk(
  'competitionsApi/createNewCompetitionThunk',
  async (form: ICreateNewCompetition.Params, thunkAPI) => {
    try {
      const body = new FormData();
      body.append('app_type', 'b2b');
      body.append('objective_id', form.objective_id.toString());

      (Object.keys(form) as Array<keyof ICreateNewCompetition.Params>).forEach(
        // @ts-ignore
        (key) => form[key] !== undefined && key !== 'status' && body.append(key, form[key]),
      );

      const data: AxiosResponse<ICreateNewCompetition.Response> = await mainServiceWithCredentials.post(
        'competitions/admin/competition/',
        body,
      );

      thunkAPI.dispatch(setDrawerOpen(false));
      thunkAPI.dispatch(competitionsB2BApi.util.invalidateTags(['allCompetitionsB2B']));
      toast.success('Соревнование создано');

      return data.data;
    } catch {
      thunkAPI.rejectWithValue(undefined);
      toast.error('Ошибка! Соревнование не создано');
    }
  },
);

const updateCompetitionThunk = createAsyncThunk(
  'competitionsApi/updateCompetitionsThunk',
  async (form: IChangeCompetition.Params, thunkAPI) => {
    try {
      const body = new FormData();
      body.append('objective_id', form.objective_id.toString());

      (Object.keys(form) as Array<keyof IChangeCompetition.Params>).forEach(
        // @ts-ignore
        (key) => form[key] !== undefined && key !== 'status' && body.append(key, form[key]),
      );

      const data: AxiosResponse<IChangeCompetition.Response> = await mainServiceWithCredentials.patch(
        `competitions/admin/competition/${form.id}/`,
        body,
      );

      thunkAPI.dispatch(setDrawerOpen(false));
      thunkAPI.dispatch(competitionsB2BApi.util.invalidateTags(['allCompetitionsB2B']));
      toast.success('Соревнование изменено');

      return data.data;
    } catch {
      toast.error('При сохранении изменений произошла ошибка');
    }
  },
);

const updateCompetitionStatusThunk = createAsyncThunk(
  'competitionsApi/updateCompetitionStatusThunk',
  async (dto: MChangeCompetitionStatus.Params, thunkAPI) => {
    try {
      await mainServiceWithCredentials.patch(`competitions/admin/competition/${dto.competition_id}/status`, {
        data: dto.status,
      });

      thunkAPI.dispatch(setDrawerOpen(false));
      thunkAPI.dispatch(competitionsB2BApi.util.invalidateTags(['allCompetitionsB2B', 'competition']));

      toast.success('Статус успешно изменен');
    } catch (e) {
      toast.error('При изменении статуса произошла ошибка');
      return handleErrorThunk(e, thunkAPI as RootThunkApiWithReject);
    }
  },
);

const addParticipantsToCompetitionsThunk = createAsyncThunk(
  'competitionsApi/createNewCompetitionThunk',
  async (form: IAddParticipantsToCompetitions.Params, thunkAPI) => {
    try {
      const body = new FormData();

      (Object.keys(form) as Array<keyof IAddParticipantsToCompetitions.Params>).forEach(
        // @ts-ignore
        (key) => form[key] !== undefined && body.append(key, form[key]),
      );

      const data: AxiosResponse<IAddParticipantsToCompetitions.Response> = await mainServiceWithCredentials.post(
        `competitions/admin/competition/${form.competition_id}/participant-xlsx`,
        body,
      );

      thunkAPI.dispatch(setDrawerOpen(false));
      thunkAPI.dispatch(competitionsB2BApi.util.invalidateTags(['competition']));

      toast.success(`Назначено ${data.data.new_participants_count} новых участников`);
      return data.data;
    } catch (e) {
      const { detail } = ((e as AxiosError).response as any).data as ResponseType;

      detail?.map((error) => {
        const { msg, loc } = error;

        if (!loc.length) {
          toast.error(msg, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      });
      thunkAPI.dispatch(setDrawerOpen(false));
    }
  },
);

export {
  createNewCompetitionThunk,
  updateCompetitionThunk,
  updateCompetitionStatusThunk,
  addParticipantsToCompetitionsThunk,
};
